import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/site";
import { getAttribution } from "@/lib/utm";

const LOAN_TYPES = [
  { v: "home_loan", l: "Home Loan" },
  { v: "business_loan", l: "Business Loan" },
  { v: "lap_lrd", l: "LAP / LRD" },
  { v: "project_finance", l: "Project Finance" },
  { v: "personal_loan", l: "Personal Loan" },
  { v: "balance_transfer", l: "Balance Transfer" },
  { v: "other", l: "Other / Not sure" },
];
const EMP_TYPES = [
  { v: "salaried", l: "Salaried" },
  { v: "self_employed", l: "Self-Employed / Professional" },
  { v: "business_owner", l: "Business Owner / MSME" },
  { v: "property_investor", l: "Property Investor" },
  { v: "other", l: "Other" },
];
const RESIDENCE = [
  { v: "owned", l: "Owned" },
  { v: "rented", l: "Rented" },
  { v: "parents", l: "Parents' / family" },
  { v: "company_provided", l: "Company-provided" },
  { v: "other", l: "Other" },
];
const BUREAU = ["750+", "700-749", "650-699", "600-649", "Below 600", "Don't know"];

const STEPS = [
  { n: 1, t: "Loan Need" },
  { n: 2, t: "Personal" },
  { n: 3, t: "Income" },
  { n: 4, t: "Liabilities & Collateral" },
  { n: 5, t: "Review & Submit" },
];

const initial = {
  // Step 1
  loan_type: "",
  loan_amount: "",
  tenure_months_requested: "",
  loan_purpose: "",
  // Step 2
  name: "",
  email: "",
  phone: "",
  dob: "",
  gender: "",
  pan: "",
  city: "Bangalore",
  pincode: "",
  residence_type: "",
  years_at_current: "",
  // Step 3
  employment_type: "",
  company_name: "",
  designation: "",
  work_exp_years: "",
  net_monthly_takehome: "",
  salary_mode: "bank_transfer",
  business_name: "",
  business_vintage_years: "",
  annual_turnover: "",
  gst_registered: false,
  gstin: "",
  // Step 4
  existing_emis_monthly: "",
  existing_loans_text: "",
  credit_card_limit_total: "",
  credit_card_outstanding: "",
  bureau_score_known: "",
  has_property_collateral: false,
  property_value: "",
  property_city: "",
  // Step 5
  referrer_name: "",
  referrer_phone: "",
  message: "",
  consent_credit_pull: false,
  consent_terms: false,
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const yes = (v) => !!v;

function validateStep(step, f) {
  if (step === 1) {
    if (!f.loan_type) return "Choose a loan type.";
    return null;
  }
  if (step === 2) {
    if (!f.name || f.name.trim().length < 2) return "Enter your full name.";
    if (!f.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return "Enter a valid email.";
    if (!f.phone || f.phone.replace(/\D/g, "").length < 10) return "Enter a 10-digit phone.";
    if (f.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan.toUpperCase())) return "PAN looks invalid.";
    return null;
  }
  if (step === 3) {
    if (!f.employment_type) return "Choose your employment type.";
    if (f.employment_type === "salaried" && !f.net_monthly_takehome) return "Enter your monthly take-home.";
    if ((f.employment_type === "self_employed" || f.employment_type === "business_owner") && !f.annual_turnover) return "Enter annual turnover or receipts.";
    return null;
  }
  if (step === 5) {
    if (!f.consent_credit_pull) return "Please consent to credit-bureau verification.";
    if (!f.consent_terms) return "Please accept the terms.";
    return null;
  }
  return null;
}

export default function ApplyPage() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    const t = params.get("type");
    const amt = params.get("amount");
    let initialForm = { ...initial };
    if (t && LOAN_TYPES.find((x) => x.v === t)) initialForm.loan_type = t;
    if (amt && /^\d+$/.test(amt)) {
      const n = parseInt(amt, 10);
      if (n >= 1_00_000) {
        initialForm.loan_amount = n >= 1_00_00_000 ? `₹${(n / 1_00_00_000).toFixed(2)} Cr` : `₹${(n / 1_00_000).toFixed(2)} L`;
      }
    }
    return initialForm;
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: val }));
  };
  const setBool = (k) => (v) => setForm((p) => ({ ...p, [k]: !!v }));

  const next = () => {
    const err = validateStep(step, form);
    if (err) return toast.error(err);
    setStep((s) => Math.min(STEPS.length, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const err = validateStep(5, form);
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const attr = getAttribution();
      const payload = {
        application_type: "full",
        apply_method: "form",
        name: form.name,
        email: form.email,
        phone: form.phone,
        loan_type: form.loan_type,
        loan_amount: form.loan_amount || null,
        employment_type: form.employment_type,
        city: form.city || "Bangalore",
        message: form.message || null,
        tenure_months_requested: num(form.tenure_months_requested),
        loan_purpose: form.loan_purpose || null,
        dob: form.dob || null,
        gender: form.gender || null,
        pan: form.pan ? form.pan.toUpperCase() : null,
        pincode: form.pincode || null,
        residence_type: form.residence_type || null,
        years_at_current: num(form.years_at_current),
        company_name: form.company_name || null,
        designation: form.designation || null,
        work_exp_years: num(form.work_exp_years),
        net_monthly_takehome: num(form.net_monthly_takehome),
        salary_mode: form.salary_mode || null,
        business_name: form.business_name || null,
        business_vintage_years: num(form.business_vintage_years),
        annual_turnover: num(form.annual_turnover),
        gst_registered: yes(form.gst_registered),
        gstin: form.gstin || null,
        existing_emis_monthly: num(form.existing_emis_monthly),
        existing_loans_text: form.existing_loans_text || null,
        credit_card_limit_total: num(form.credit_card_limit_total),
        credit_card_outstanding: num(form.credit_card_outstanding),
        bureau_score_known: form.bureau_score_known || null,
        has_property_collateral: yes(form.has_property_collateral),
        property_value: num(form.property_value),
        property_city: form.property_city || null,
        referrer_name: form.referrer_name || null,
        referrer_phone: form.referrer_phone || null,
        consent_credit_pull: yes(form.consent_credit_pull),
        consent_terms: yes(form.consent_terms),
        persona: attr.persona,
        landing_page: attr.landing_page || window.location.pathname,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_content: attr.utm_content,
        utm_term: attr.utm_term,
        referrer: attr.referrer,
      };
      await api.post("/inquiries", payload);
      setDone(true);
      toast.success("Application received. Raj will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="container-x py-24 md:py-32">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand-terracotta/10 text-brand-terracotta">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 h-display text-4xl sm:text-5xl">Application received.</h1>
          <p className="mt-5 text-brand-sage text-lg leading-relaxed">
            Raj has been notified. Expect a call from{" "}
            <span className="font-semibold text-brand-forest">{BRAND.phone}</span> within one
            business day to review your file and request the supporting documents over WhatsApp / email.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={`tel:${BRAND.phoneE164}`} className="btn-primary"><Phone className="h-4 w-4" /> Call Raj</a>
            <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" className="btn-secondary">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Link to="/" className="btn-ghost">Back to home <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="container-x py-12 md:py-16">
      <div className="max-w-3xl mx-auto">
        <span className="eyebrow">Loan Application</span>
        <h1 className="mt-4 h-display text-3xl sm:text-4xl">
          Step-by-step loan application.
        </h1>
        <p className="mt-3 text-brand-sage">
          Takes 4–5 minutes. We use this to match you to the best-fit lender — Raj will collect documents over WhatsApp / email after the call.
        </p>

        <Stepper step={step} />

        <div className="mt-8 card-soft p-6 md:p-8 space-y-6" data-testid={`apply-step-${step}`}>
          {step === 1 && <Step1 form={form} set={set} />}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} setBool={setBool} />}
          {step === 4 && <Step4 form={form} set={set} setBool={setBool} />}
          {step === 5 && <Step5 form={form} set={set} setBool={setBool} />}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            onClick={back}
            disabled={step === 1}
            data-testid="apply-back-btn"
            className="btn-ghost disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length ? (
            <button onClick={next} data-testid="apply-next-btn" className="btn-primary">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="apply-submit-btn"
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : (<>Submit application <ArrowRight className="h-4 w-4" /></>)}
            </button>
          )}
        </div>

        <p className="mt-8 text-xs text-brand-sage text-center max-w-xl mx-auto">
          Your details are used only for this loan inquiry. We do NOT sell or share your data.
          You'll be asked for documents (PAN, Aadhaar, bank statements, ITR/payslips, etc.) over a secure WhatsApp / email channel after the first call.
        </p>
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="mt-8" data-testid="apply-stepper">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex-1 flex items-center gap-2">
            <div className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold shrink-0 ${s.n < step ? "bg-brand-terracotta text-white" : s.n === step ? "bg-brand-forest text-brand-gold ring-2 ring-brand-gold" : "bg-brand-sand text-brand-sage"}`}>
              {s.n < step ? "✓" : s.n}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 rounded-full ${s.n < step ? "bg-brand-terracotta" : "bg-brand-sand"}`} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="font-semibold text-brand-forest">{STEPS[step - 1].t}</span>
        <span className="text-brand-sage">Step {step} of {STEPS.length}</span>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-brand-forest">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-brand-sage">{hint}</p>}
    </div>
  );
}

function Step1({ form, set }) {
  return (
    <>
      <H>What loan do you need?</H>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Loan type *" htmlFor="loan_type">
          <Select value={form.loan_type} onValueChange={(v) => set("loan_type")(v)}>
            <SelectTrigger id="loan_type" data-testid="apply-loan-type"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>{LOAN_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Loan amount" htmlFor="loan_amount">
          <Input id="loan_amount" data-testid="apply-amount" value={form.loan_amount} onChange={set("loan_amount")} placeholder="e.g. ₹50 Lakh" />
        </Field>
        <Field label="Preferred tenure (months)" htmlFor="tenure">
          <Input id="tenure" type="number" min="6" max="360" data-testid="apply-tenure" value={form.tenure_months_requested} onChange={set("tenure_months_requested")} placeholder="e.g. 240 for home loan" />
        </Field>
        <Field label="Purpose / use of funds" htmlFor="purpose">
          <Input id="purpose" data-testid="apply-purpose" value={form.loan_purpose} onChange={set("loan_purpose")} placeholder="e.g. Working capital, BT, machinery" />
        </Field>
      </div>
    </>
  );
}

function Step2({ form, set }) {
  return (
    <>
      <H>Personal details</H>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name *" htmlFor="name">
          <Input id="name" data-testid="apply-name" value={form.name} onChange={set("name")} placeholder="As per PAN" />
        </Field>
        <Field label="Phone *" htmlFor="phone">
          <Input id="phone" data-testid="apply-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
        </Field>
        <Field label="Email *" htmlFor="email">
          <Input id="email" type="email" data-testid="apply-email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </Field>
        <Field label="Date of birth" htmlFor="dob">
          <Input id="dob" type="date" data-testid="apply-dob" value={form.dob} onChange={set("dob")} />
        </Field>
        <Field label="Gender" htmlFor="gender">
          <Select value={form.gender} onValueChange={(v) => set("gender")(v)}>
            <SelectTrigger id="gender" data-testid="apply-gender"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
              <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="PAN" htmlFor="pan" hint="10 characters, e.g. ABCDE1234F">
          <Input id="pan" data-testid="apply-pan" value={form.pan} onChange={(e) => set("pan")(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
        </Field>
        <Field label="City" htmlFor="city">
          <Input id="city" data-testid="apply-city" value={form.city} onChange={set("city")} />
        </Field>
        <Field label="PIN code" htmlFor="pincode">
          <Input id="pincode" data-testid="apply-pincode" value={form.pincode} onChange={set("pincode")} maxLength={6} placeholder="6-digit" />
        </Field>
        <Field label="Residence type" htmlFor="restype">
          <Select value={form.residence_type} onValueChange={(v) => set("residence_type")(v)}>
            <SelectTrigger id="restype" data-testid="apply-residence"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{RESIDENCE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Years at current address" htmlFor="years_addr">
          <Input id="years_addr" type="number" step="0.5" data-testid="apply-yearsaddr" value={form.years_at_current} onChange={set("years_at_current")} />
        </Field>
      </div>
    </>
  );
}

function Step3({ form, set, setBool }) {
  const isSalaried = form.employment_type === "salaried";
  const isBusiness = form.employment_type === "self_employed" || form.employment_type === "business_owner";
  return (
    <>
      <H>Income & employment</H>
      <Field label="Employment type *" htmlFor="emp">
        <Select value={form.employment_type} onValueChange={(v) => set("employment_type")(v)}>
          <SelectTrigger id="emp" data-testid="apply-emp-type"><SelectValue placeholder="How do you earn?" /></SelectTrigger>
          <SelectContent>{EMP_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
      </Field>

      {isSalaried && (
        <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="apply-salaried-block">
          <Field label="Employer / Company"><Input data-testid="apply-company" value={form.company_name} onChange={set("company_name")} placeholder="e.g. Infosys" /></Field>
          <Field label="Designation"><Input data-testid="apply-designation" value={form.designation} onChange={set("designation")} placeholder="e.g. Senior Engineer" /></Field>
          <Field label="Total work experience (years)"><Input type="number" step="0.5" data-testid="apply-workexp" value={form.work_exp_years} onChange={set("work_exp_years")} /></Field>
          <Field label="Net monthly take-home (₹) *" hint="In-hand salary after PF, tax, deductions"><Input type="number" data-testid="apply-takehome" value={form.net_monthly_takehome} onChange={set("net_monthly_takehome")} placeholder="e.g. 150000" /></Field>
          <Field label="Salary mode">
            <RadioGroup value={form.salary_mode} onValueChange={(v) => set("salary_mode")(v)} className="flex flex-wrap gap-3 pt-1">
              {[["bank_transfer", "Bank transfer"], ["cheque", "Cheque"], ["cash", "Cash"]].map(([v, l]) => (
                <label key={v} className="inline-flex items-center gap-1.5 text-sm">
                  <RadioGroupItem value={v} data-testid={`apply-salmode-${v}`} /> {l}
                </label>
              ))}
            </RadioGroup>
          </Field>
        </div>
      )}

      {isBusiness && (
        <div className="grid sm:grid-cols-2 gap-4 pt-2" data-testid="apply-business-block">
          <Field label="Business / Firm name"><Input data-testid="apply-bizname" value={form.business_name} onChange={set("business_name")} placeholder="Firm name" /></Field>
          <Field label="Business vintage (years)"><Input type="number" step="0.5" data-testid="apply-bizvintage" value={form.business_vintage_years} onChange={set("business_vintage_years")} /></Field>
          <Field label="Annual turnover / receipts (₹) *"><Input type="number" data-testid="apply-turnover" value={form.annual_turnover} onChange={set("annual_turnover")} placeholder="e.g. 25000000" /></Field>
          <Field label="GST registered?">
            <div className="flex items-center gap-2 pt-2.5">
              <Checkbox id="gstreg" data-testid="apply-gst" checked={!!form.gst_registered} onCheckedChange={setBool("gst_registered")} />
              <label htmlFor="gstreg" className="text-sm">Yes, I file GST returns</label>
            </div>
          </Field>
          {form.gst_registered && (
            <Field label="GSTIN (optional)"><Input data-testid="apply-gstin" value={form.gstin} onChange={set("gstin")} placeholder="29ABCDE1234F1Z5" maxLength={15} /></Field>
          )}
        </div>
      )}

      {(form.employment_type === "property_investor" || form.employment_type === "other") && (
        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <Field label="Approx. monthly income (₹)"><Input type="number" data-testid="apply-monthly-income" value={form.net_monthly_takehome} onChange={set("net_monthly_takehome")} /></Field>
        </div>
      )}
    </>
  );
}

function Step4({ form, set, setBool }) {
  return (
    <>
      <H>Existing liabilities & collateral</H>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Total existing EMIs / month (₹)"><Input type="number" data-testid="apply-emis" value={form.existing_emis_monthly} onChange={set("existing_emis_monthly")} placeholder="e.g. 25000" /></Field>
        <Field label="Existing loans (short note)"><Input data-testid="apply-existloans" value={form.existing_loans_text} onChange={set("existing_loans_text")} placeholder="e.g. HL ₹40L, Car ₹4L" /></Field>
        <Field label="Total credit-card limit (₹)"><Input type="number" data-testid="apply-cc-limit" value={form.credit_card_limit_total} onChange={set("credit_card_limit_total")} /></Field>
        <Field label="Credit-card outstanding (₹)"><Input type="number" data-testid="apply-cc-os" value={form.credit_card_outstanding} onChange={set("credit_card_outstanding")} /></Field>
        <Field label="Known credit score">
          <Select value={form.bureau_score_known} onValueChange={(v) => set("bureau_score_known")(v)}>
            <SelectTrigger data-testid="apply-bureau"><SelectValue placeholder="If known" /></SelectTrigger>
            <SelectContent>{BUREAU.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <div className="mt-4 border-t border-brand-sand pt-5 space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox id="collat" data-testid="apply-has-collateral" checked={!!form.has_property_collateral} onCheckedChange={setBool("has_property_collateral")} />
          <label htmlFor="collat" className="text-sm font-medium text-brand-forest">
            I have a property to offer as collateral (for LAP / LRD / secured loans)
          </label>
        </div>
        {form.has_property_collateral && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Approx. property value (₹)"><Input type="number" data-testid="apply-propval" value={form.property_value} onChange={set("property_value")} placeholder="e.g. 20000000" /></Field>
            <Field label="Property city"><Input data-testid="apply-propcity" value={form.property_city} onChange={set("property_city")} placeholder="e.g. Bangalore" /></Field>
          </div>
        )}
      </div>
    </>
  );
}

function Step5({ form, set, setBool }) {
  const summary = useMemo(() => {
    const lt = LOAN_TYPES.find((x) => x.v === form.loan_type)?.l || form.loan_type;
    const et = EMP_TYPES.find((x) => x.v === form.employment_type)?.l || form.employment_type;
    return [
      ["Loan type", lt],
      ["Amount sought", form.loan_amount || "—"],
      ["Tenure", form.tenure_months_requested ? `${form.tenure_months_requested} months` : "—"],
      ["Purpose", form.loan_purpose || "—"],
      ["Name", form.name],
      ["Phone", form.phone],
      ["Email", form.email],
      ["City / PIN", `${form.city || "—"} ${form.pincode ? `/ ${form.pincode}` : ""}`.trim()],
      ["Employment", et],
      ["Net monthly income", form.net_monthly_takehome ? `₹${Number(form.net_monthly_takehome).toLocaleString("en-IN")}` : "—"],
      ["Annual turnover", form.annual_turnover ? `₹${Number(form.annual_turnover).toLocaleString("en-IN")}` : "—"],
      ["Existing EMIs/mo", form.existing_emis_monthly ? `₹${Number(form.existing_emis_monthly).toLocaleString("en-IN")}` : "—"],
      ["Collateral", form.has_property_collateral ? `Yes — ₹${form.property_value ? Number(form.property_value).toLocaleString("en-IN") : "?"} in ${form.property_city || "—"}` : "No"],
    ];
  }, [form]);
  return (
    <>
      <H>Review & consent</H>
      <div className="rounded-2xl bg-brand-sand/40 border border-brand-sand p-5">
        <h4 className="font-outfit font-semibold text-brand-forest text-sm uppercase tracking-[0.16em] text-brand-terracotta">Summary</h4>
        <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {summary.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-brand-sand py-1.5">
              <dt className="text-brand-sage">{k}</dt>
              <dd className="font-medium text-brand-forest text-right truncate max-w-[60%]" title={String(v)}>{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 pt-2">
        <Field label="Referrer's name (if any)"><Input data-testid="apply-ref-name" value={form.referrer_name} onChange={set("referrer_name")} placeholder="Who referred you?" /></Field>
        <Field label="Referrer's phone"><Input data-testid="apply-ref-phone" value={form.referrer_phone} onChange={set("referrer_phone")} /></Field>
      </div>

      <Field label="Anything else we should know?">
        <Textarea data-testid="apply-message" value={form.message} onChange={set("message")} className="min-h-[90px]" placeholder="Special situation, timeline, lender preference, past rejections, etc." />
      </Field>

      <div className="space-y-3 pt-3 border-t border-brand-sand">
        <label className="flex items-start gap-2 text-sm text-brand-forest/85">
          <Checkbox data-testid="apply-consent-credit" checked={!!form.consent_credit_pull} onCheckedChange={setBool("consent_credit_pull")} className="mt-0.5" />
          <span>I authorise Swift Financial Solutions to share my details with select banks / NBFCs and to obtain my credit-bureau report for the purpose of this loan inquiry. <span className="text-brand-terracotta">*</span></span>
        </label>
        <label className="flex items-start gap-2 text-sm text-brand-forest/85">
          <Checkbox data-testid="apply-consent-terms" checked={!!form.consent_terms} onCheckedChange={setBool("consent_terms")} className="mt-0.5" />
          <span>I accept that loan sanction is at the lender's discretion. Swift Financial acts only as a facilitator and does not guarantee approval. <span className="text-brand-terracotta">*</span></span>
        </label>
      </div>
    </>
  );
}

function H({ children }) {
  return <h2 className="font-outfit font-bold text-2xl text-brand-forest">{children}</h2>;
}
